import React, { useCallback } from "react";

import usePDFStyles from "@Components/shared/hooks/usePDFStyles";
import PDFSVGs from "@Components/shared/PDFSVGs";
import { IPdfItem, IPdfItemInclStyles, IPDFItemStyle } from "@Models/IPdfItem";
import { Page as BasePage, Text as BaseText, Document, Font, Image, View } from "@react-pdf/renderer";
import { convertFieldToPdfComponent, DateTime, isValidIsoDate, toCamelCase, toPascalCase } from "@Utils";
import { PublicAssetsUrl } from "@Constants";
import { IFieldData } from "@Components/shared/types";

const LetterheadImage = PublicAssetsUrl + "/print/inovus-a4-letterhead-1.png";
const SignatureImage = PublicAssetsUrl + "/print/cpd-signature.png";
const GLCLogo = PublicAssetsUrl + "/print/GLCLogo.png";

interface BlockItem extends IFullPdfItem {
    blockStyle: IPDFItemStyle;
}

interface IItem {
    item: IPdfItemInclStyles;
}

interface IFullPdfItem extends IItem {
    arr: IPdfItemInclStyles[];
    index: number;
    parentStyle?: IPDFItemStyle;
}

type ITheme = "default" | "certificate";
type IReportType = "assessment" | "compliance" | "points" | "cme";

interface IUserStyledPDFProps {
    report?: IReportType;
    theme?: ITheme;
}

const ROBOTO_URLS = {
    "100": "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1MmgWxPKTM1K9nz.ttf",
    "100italic": "https://fonts.gstatic.com/s/roboto/v30/KFOiCnqEu92Fr1Mu51QrIzcXLsnzjYk.ttf",
    "300": "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5vAx05IsDqlA.ttf",
    "300italic": "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TjARc9AMX6lJBP.ttf",
    regular: "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5WZLCzYlKw.ttf",
    italic: "https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu52xPKTM1K9nz.ttf",
    "500": "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9vAx05IsDqlA.ttf",
    "500italic": "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51S7ABc9AMX6lJBP.ttf",
    "700": "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlvAx05IsDqlA.ttf",
    "700italic": "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TzBhc9AMX6lJBP.ttf",
    "900": "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmYUtvAx05IsDqlA.ttf",
    "900italic": "https://fonts.gstatic.com/s/roboto/v30/KFOjCnqEu92Fr1Mu51TLBBc9AMX6lJBP.ttf",
};

const BORDER_COLOR = "#D9D9D9";

const images = {
    SignatureImage,
};

const checkNumberOrder = (arr: IPdfItemInclStyles[], index: number, count = 1): number =>
    arr?.[index - 1]?.style === "numbered" ? checkNumberOrder(arr, index - 1, count + 1) : count;

/**
 * Format text if it's a date
 */
const formatText = (text = "") => (!!text && isValidIsoDate(text) ? DateTime(text).format("MMM Do YYYY") : text);

const useStyledPDF = ({ report, theme = "default" }: IUserStyledPDFProps) => {
    const { InovusLogo, RCSLogo } = PDFSVGs;
    const { getStyles, styles } = usePDFStyles(report, theme);

    Font?.register({ family: "Roboto", src: ROBOTO_URLS.regular });
    Font?.register({ src: ROBOTO_URLS[700], family: "RobotoBold", fontWeight: 700 });
    Font?.register({
        src: ROBOTO_URLS["700italic"],
        family: "RobotoBoldItalic",
        fontStyle: "italic",
        fontWeight: 700,
    });
    Font?.register({
        src: ROBOTO_URLS.italic,
        family: "RobotoItalic",
    });

    /**
     * Adds in PDF elements
     */
    const createPdfElement = (
        item: IPdfItemInclStyles,
        index: number,
        arr: IPdfItemInclStyles[],
        parentStyle?: IPDFItemStyle
    ) => {
        switch (item.style) {
            case "break":
                return <View break key={index} />;
            case "block":
            case "centred":
            case "left":
            case "right":
                const numberOfSections = Number(item.value ?? 1);
                // will only be a split block if the value is explicitly as a number greater than 1
                const BlockType = numberOfSections > 1 ? SplitBlock : Block;
                return (
                    <BlockType
                        arr={arr}
                        blockStyle={item.style}
                        index={index}
                        item={item}
                        parentStyle={parentStyle}
                        key={index}
                    />
                );
            case "image":
                const imageSource = images[toPascalCase(item.value)];
                const imageStyle = styles[toCamelCase(item.value)];
                return !!imageSource ? <Image style={imageStyle} source={imageSource} key={index} /> : null;
            case "json":
                return <JSONData arr={arr} item={item} index={index} key={index} />;
            case "bullet":
            case "numbered":
                return <ListItem arr={arr} item={item} index={index} key={index} />;
            case "page":
                return <Page arr={arr} item={item} index={index} key={index} />;
            case "svg":
                const SvgSource = PDFSVGs[toPascalCase(item.value)];
                const svgStyle = styles[toCamelCase(item.value)];
                return !!SvgSource ? <SvgSource style={svgStyle} key={index} /> : null;
            case "tableRow":
                return <TableRow arr={arr} index={index} item={item} key={index} />;
            default:
                return <Text item={item} key={index} />;
        }
    };

    /**
     * A section of the PDF that contains nested items
     *
     */
    const Block: React.FC<BlockItem> = ({ arr, blockStyle, item, parentStyle, ...otherProps }) => (
        <View style={styles[parentStyle] ?? {}} {...otherProps}>
            <View style={styles[`${blockStyle}View`] ?? {}} {...otherProps}>
                {item?.items?.map((textOrItem, itemIndex) =>
                    !!textOrItem?.items ? (
                        createPdfElement(textOrItem, itemIndex, arr, blockStyle)
                    ) : (
                        <View key={itemIndex} style={styles[blockStyle]}>
                            {createPdfElement(textOrItem, itemIndex, [])}
                        </View>
                    )
                )}
            </View>
        </View>
    );

    /**
     * A split section of the PDF that contains nested items
     *
     */
    const SplitBlock: React.FC<BlockItem> = ({ arr, blockStyle, item, parentStyle, ...otherProps }) => {
        const numberOfSections = Number(item.value ?? 1);
        return (
            <View style={numberOfSections > 1 ? { flexDirection: "row" } : {}}>
                {numberOfSections}
                {[...Array(numberOfSections).keys()].map((key) => {
                    const width = `${100 / numberOfSections}%`;
                    const mergedStyles = { ...styles?.[parentStyle], width };
                    const section = item.items[key];
                    // TODO: review applying parent style
                    return (
                        <View key={`block-section-${key}`} style={mergedStyles} {...otherProps}>
                            <View style={styles[`${blockStyle}View`] ?? {}} {...otherProps}>
                                {section?.items ? (
                                    createPdfElement(section, key, arr, blockStyle)
                                ) : (
                                    <View key={key} style={styles[blockStyle]}>
                                        {createPdfElement(section, key, [])}
                                    </View>
                                )}
                            </View>
                        </View>
                    );
                })}
            </View>
        );
    };

    const JSONData: React.FC<IFullPdfItem> = ({ arr, children, index, item, ...otherProps }) => {
        const parsed: IFieldData[] = JSON.parse(item.value);
        return (
            <View style={{ borderColor: BORDER_COLOR, borderWidth: 1 }}>
                {parsed.map((parsedValue) => {
                    parsedValue.style = { ...parsedValue.style, ...styles.p, marginLeft: 5 };
                    return (
                        <View style={{ ...styles.tableRow, borderWidth: 0, alignItems: "center" }} key={parsedValue.id}>
                            <View style={{ ...styles.tableCell, borderWidth: 0 }}>
                                <BaseText style={styles.h6}>{`${parsedValue.name}: `}</BaseText>
                            </View>
                            <View style={{ ...styles.tableCell, borderWidth: 0, flexGrow: 3 }}>
                                {convertFieldToPdfComponent(parsedValue)}
                            </View>
                        </View>
                    );
                })}
            </View>
        );
    };

    const ListItem: React.FC<IFullPdfItem> = ({ arr, children, index, item, ...otherProps }) => {
        const { style, value } = item;

        const nextStyle = arr?.[index + 1]?.style;
        const isLastPoint = typeof nextStyle !== "string" || !["bullet", "numbered"].includes(nextStyle);
        const cardinal = checkNumberOrder(arr, index);
        const point = style === "bullet" ? "•" : cardinal;

        return (
            <BaseText style={{ ...getStyles(style), ...(isLastPoint ? { marginBottom: 8 } : {}) }} {...otherProps}>
                <BaseText>{`${point}     `}</BaseText>
                <BaseText>{value}</BaseText>
            </BaseText>
        );
    };

    const TableRow: React.FC<IFullPdfItem> = ({ arr, index, item, ...otherProps }) => {
        const prevStyle = arr?.[index - 1]?.style;
        const isFirstRow = typeof prevStyle !== "string" || prevStyle !== "tableRow";
        const nextStyle = arr?.[index + 1]?.style;
        const isLastRow = typeof nextStyle !== "string" || nextStyle !== "tableRow";

        const tableStyle = {
            ...styles.tableRow,
            ...(isFirstRow ? { borderTopColor: BORDER_COLOR, borderTopWidth: 1 } : {}),
            ...(isLastRow ? { marginBottom: 12 } : {}),
        };
        return (
            <View style={tableStyle} {...otherProps} wrap={false}>
                {item.items.map((cell, itemIndex) => {
                    const borderLeft = itemIndex === 0 ? {} : {};
                    const style = { ...getStyles(cell.style), ...borderLeft, ...{ marginBottom: 0, lineHeight: 1 } };
                    cell.style = style;
                    return (
                        <View key={itemIndex} style={styles.tableCell}>
                            <Text key={itemIndex} item={cell} />
                        </View>
                    );
                })}
            </View>
        );
    };

    const Text: React.FC<IItem> = ({ item, ...otherProps }) => (
        <BaseText style={{ ...getStyles(item.style) }} {...otherProps}>
            {formatText(item.value)}
        </BaseText>
    );

    const Page: React.FC<IFullPdfItem> = ({ item: page, index }) => (
        <BasePage size="A4" style={styles.body}>
            <View style={styles.viewbox}>
                {page.items.map((item, itemIndex) => createPdfElement(item, itemIndex, item.items))}
                {pageWrap(page, index)}
                {pageFooter(page, index)}
            </View>
        </BasePage>
    );

    /**
     * Add a border to the page depending on the theme
     * @param page
     * @param pageNumber starts at 0
     */
    const pageWrap = (_page: IPdfItemInclStyles, _pageNumber: number) => {
        if (theme === "certificate") {
            return <View style={styles.pageWrap} fixed />;
        }

        return null;
    };

    /**
     * adds a fixed footer to the bottom of the page
     * @param page
     * @param pageNumber starts at 0
     */
    const pageFooter = (_page: IPdfItemInclStyles, pageNumber: number) => {
        if (report === "points" && pageNumber === 0) {
            return (
                <View style={styles.footerView}>
                    <RCSLogo style={styles.footerLogo} />
                    <InovusLogo style={styles.footerLogo} />
                </View>
            );
        }
        if (report === "cme" && pageNumber === 0) {
            return (
                <View style={styles.footerView}>
                    <Image style={styles.glcLogo} source={GLCLogo} />
                    <InovusLogo style={styles.footerLogo} />
                </View>
            );
        }

        return null;
    };

    const singlePage = (content: IPdfItem[]) => (
        <Document>
            <BasePage size="A4" style={styles.body}>
                {theme === "default" ? ( // default page with letterhead
                    <View style={styles.section}>
                        <Image style={styles.letterhead} source={LetterheadImage} />
                    </View>
                ) : null}
                <View style={styles.viewbox}>{content.map(createPdfElement)}</View>
            </BasePage>
        </Document>
    );

    return useCallback(
        ({ content = [] }: { content: IPdfItem[] }) => {
            const isSinglePage = content?.[0]?.style !== "page"; // a page may span across numerous pages
            return isSinglePage ? singlePage(content) : <Document>{content.map(createPdfElement)}</Document>;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
};

export default useStyledPDF;
